import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { addErrorNotification } from 'shared/lib/notifications';
import CloseButton from 'shared/components/buttons/Close';
import styles from './ButtonWithModal.module.scss';

export const ButtonWithModal = ({
  modalTitle,
  buttonComponent,
  buttonTitle,
  render,
  buttonIconClassName,
  modalSize,
  closeModalTitle,
  buttonDisabled,
  buttonClassName,
  fullWidthButton,
  notAllowedNotification,
  canCloseByOutsideClick,
  fullHeightModal,
  autoScrollDown,
  noHeader,
  noBodyPadding,
}) => {
  const [opened, setOpened] = useState(false);
  const autoScrollEnabled = useRef(true);
  const autoScrolling = useRef(false);

  useEffect(() => {
    if (opened) {
      autoScrollEnabled.current = true;
    }
  }, [opened]);

  const toggleModal = () => {
    if (notAllowedNotification) {
      addErrorNotification(notAllowedNotification);
      return;
    }
    setOpened(prev => !prev);
  };

  const closeModal = () => setOpened(false);

  const handleScroll = () => {
    if (autoScrolling.current) return;

    autoScrollEnabled.current = false;
    try {
      const windowRect = document.getElementById('sc-modal-body').getBoundingClientRect();
      const endRect = document.getElementById('sc-modal-content-end').getBoundingClientRect();
      if (endRect.top <= windowRect.top + windowRect.height) {
        autoScrollEnabled.current = true;
      }
    } catch {
      autoScrollEnabled.current = true;
    }
  };

  const onContentChange = useCallback(() => {
    if (!(autoScrollDown && autoScrollEnabled.current)) return;

    // Set synchronously before browser layout/scroll-anchoring events fire
    autoScrolling.current = true;
    window.requestAnimationFrame(() => {
      try {
        document.getElementById('sc-modal-body').scrollTo(0, document.getElementById('sc-modal-content').scrollHeight);
      } catch {}
      // Second rAF ensures scroll event from scrollTo has been fully processed
      window.requestAnimationFrame(() => {
        autoScrolling.current = false;
      });
    });
  }, [autoScrollDown]);

  return (
    <>
      <Modal
        centered
        scrollable
        size={modalSize}
        isOpen={opened}
        contentClassName={fullHeightModal ? styles.fullheight : undefined}
        toggle={canCloseByOutsideClick ? toggleModal : undefined}
      >
        {!noHeader && (
          <ModalHeader
            toggle={toggleModal}
            close={<CloseButton onClick={toggleModal}>{closeModalTitle}</CloseButton>}
          >
            <div className={styles.modalHeader}>
              {modalTitle}
            </div>
          </ModalHeader>
        )}
        <ModalBody id="sc-modal-body" className={noBodyPadding ? styles.noPaddingBody : undefined} onScroll={handleScroll}>
          {opened ? (
            <div id="sc-modal-content">
              {render({ closeModal, onContentChange })}
              <div id="sc-modal-content-end" />
            </div>
          ) : null}
        </ModalBody>
      </Modal>
      {buttonComponent
        ? React.cloneElement(buttonComponent, { onClick: toggleModal })
        : (
          <button disabled={buttonDisabled} type="button" className={cn(styles.button, { [styles.fullWidth]: fullWidthButton }, buttonClassName)} onClick={toggleModal}>
            {buttonIconClassName && (<i className={buttonIconClassName} />)}
            <span>{buttonTitle}</span>
          </button>
        )
      }
    </>
  );
};

ButtonWithModal.propTypes = {
  modalTitle: PropTypes.node,
  buttonComponent: PropTypes.node,
  buttonTitle: PropTypes.node,
  render: PropTypes.func.isRequired,
  buttonIconClassName: PropTypes.string,
  modalSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  closeModalTitle: PropTypes.string,
  buttonDisabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
  fullWidthButton: PropTypes.bool,
  notAllowedNotification: PropTypes.string,
  canCloseByOutsideClick: PropTypes.bool,
  fullHeightModal: PropTypes.bool,
  autoScrollDown: PropTypes.bool,
  noHeader: PropTypes.bool,
  noBodyPadding: PropTypes.bool,
};

ButtonWithModal.defaultProps = {
  modalTitle: null,
  buttonComponent: null,
  buttonTitle: null,
  buttonIconClassName: null,
  modalSize: 'md',
  closeModalTitle: 'close',
  buttonDisabled: false,
  buttonClassName: null,
  fullWidthButton: false,
  notAllowedNotification: null,
  canCloseByOutsideClick: false,
  fullHeightModal: false,
  autoScrollDown: false,
  noHeader: false,
  noBodyPadding: false,
};

export default ButtonWithModal;
